/**
 * Navigation Permission Helper
 * 
 * This helper provides permission checks for navigation menu items.
 * It's used by the navigation data to determine which items should be hidden
 * based on the current user's role and permissions.
 * 
 * IMPORTANT: This uses a static injector reference that must be initialized
 * in the AppComponent or main.ts before navigation is rendered.
 * 
 * Usage in navigation data.ts:
 * ```typescript
 * import { NavigationPermissionHelper } from 'app/core/navigation/navigation-permission.helper';
 * 
 * {
 *     id: 'administration.users',
 *     title: 'Usuarios',
 *     hidden: () => NavigationPermissionHelper.shouldHideUsersMenu(),
 *     ...
 * }
 * ```
 */

import { Injector } from '@angular/core';
import { PermissionService } from 'app/core/permissions/permission.service';

export class NavigationPermissionHelper {
    private static injector: Injector;
    
    /**
     * Initialize the helper with the application injector
     * MUST be called during app initialization
     */
    static initialize(injector: Injector): void {
        NavigationPermissionHelper.injector = injector;
    }
    
    /**
     * Get the PermissionService instance
     */
    private static getPermissionService(): PermissionService {
        if (!NavigationPermissionHelper.injector) {
            console.warn('NavigationPermissionHelper not initialized! Defaulting to showing all menu items.');
            // Return a mock that allows everything rather than crashing
            return {
                isAdmin: () => true,
                isProjectManager: () => false,
                getOwnedProjectsSync: () => []
            } as any;
        }
        return NavigationPermissionHelper.injector.get(PermissionService);
    }
    
    // ============================================================================
    // MENU VISIBILITY CHECKS
    // These return TRUE if item should be HIDDEN, FALSE if should be VISIBLE
    // ============================================================================
    
    /**
     * Should hide Users menu item
     * Only Admins can access user management
     */
    static shouldHideUsersMenu(): boolean {
        const permissionService = this.getPermissionService();
        return !permissionService.isAdmin();
    }
    
    /**
     * Should hide catalog management items
     * (Attack Types, Attack Subtypes, Honeytoken Types)
     * Per RBAC v3.0: These are master data catalogs, Admin-only for management
     */
    static shouldHideCatalogManagementMenu(): boolean {
        const permissionService = this.getPermissionService();
        // Admin-only: Catalog management is part of master data administration
        return !permissionService.isAdmin();
    }
    
    /**
     * Should hide Domains menu item
     * Only Admin can manage domains (CRUD operations)
     * Per RBAC v3.0: Domains are admin-only global resources
     */
    static shouldHideDomainsMenu(): boolean {
        const permissionService = this.getPermissionService();
        return !permissionService.isAdmin();
    }
    
    /**
     * Should hide entire Administration section
     * Only Admin can access Administration features
     */
    static shouldHideAdministrationSection(): boolean {
        const permissionService = this.getPermissionService();
        return !permissionService.isAdmin();
    }
    
    /**
     * Should hide Data Management menu item
     * Admin-only feature
     */
    static shouldHideDataManagementMenu(): boolean {
        const permissionService = this.getPermissionService();
        return !permissionService.isAdmin();
    }
    
    // ============================================================================
    // BADGE HELPERS
    // ============================================================================
    
    /**
     * Get badge for project ownership
     * Returns badge configuration for Project Managers who own projects
     */
    static getProjectOwnershipBadge(): { title: string; classes: string } | undefined {
        const permissionService = this.getPermissionService();
        
        // Only show badge for Project Managers
        if (!permissionService.isProjectManager()) {
            return undefined;
        }
        
        // Get projects where user is owner
        const ownedProjects = permissionService.getOwnedProjectsSync();
        
        if (ownedProjects.length === 0) {
            return undefined;
        }
        
        // Return badge with count of owned projects
        return {
            title: ownedProjects.length.toString(),
            classes: 'px-2 bg-primary text-on-primary rounded-full'
        };
    }
    
    /**
     * Get the appropriate project menu title based on user role
     * Admin: "Todos los Proyectos"
     * Others: "Mis Proyectos"
     */
    static getProjectMenuTitle(): string {
        const permissionService = this.getPermissionService();
        return permissionService.isAdmin() ? 'Todos los Proyectos' : 'Mis Proyectos';
    }
}
